import { useState, useEffect, useRef, useCallback } from 'react';
import { SearchIcon, FilterIcon, HeartIcon, GownIcon } from '../components/Icons';

const DEFAULT_CATEGORY_IMAGES = [
  'https://images.unsplash.com/photo-1594552072238-b8a33785b6cd?w=400&q=80',
  'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&q=80',
  'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&q=80',
  'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400&q=80',
  'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=400&q=80',
  'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400&q=80',
];

const SORT_OPTIONS = ['Default', 'Price: Low to High', 'Price: High to Low', 'Name A-Z'];

/* ─────────────────────────────────────────────────────────────
   Hero Carousel — supports fullBleed mode for desktop
   ───────────────────────────────────────────────────────────── */
function HeroCarousel({ slides, categories, onNavigate, fullBleed = false }) {
  const total = slides.length;
  const cloned = total > 0 ? [slides[total - 1], ...slides, slides[0]] : [];
  const [heroIdx, setHeroIdx] = useState(1);
  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const activeDot = heroIdx <= 0 ? total - 1 : heroIdx >= cloned.length - 1 ? 0 : heroIdx - 1;

  const goTo = useCallback((idx, animate = true) => {
    const track = trackRef.current;
    if (!track) return;
    track.style.transition = animate ? 'transform 0.48s cubic-bezier(0.4,0,0.2,1)' : 'none';
    track.style.transform = `translateX(-${(idx / cloned.length) * 100}%)`;
    setHeroIdx(idx);
  }, [cloned.length]);

  const handleTransitionEnd = () => {
    if (heroIdx >= cloned.length - 1) goTo(1, false);
    else if (heroIdx <= 0) goTo(total, false);
  };

  useEffect(() => {
    if (total <= 1) return;
    timerRef.current = setInterval(() => goTo(heroIdx + 1, true), 5000);
    return () => clearInterval(timerRef.current);
  }, [heroIdx, total, goTo]);

  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; clearInterval(timerRef.current); };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? goTo(heroIdx + 1, true) : goTo(heroIdx - 1, true);
    touchStartX.current = null;
  };

  if (total === 0) return null;

  const wrapperClass = fullBleed
    ? 'overflow-hidden w-full'
    : 'overflow-hidden mx-4 rounded-xl';

  const slideClass = fullBleed
    ? 'aspect-[21/8] relative bg-gray-100 overflow-hidden cursor-pointer'
    : 'aspect-[16/9] relative bg-gray-100 overflow-hidden cursor-pointer';

  return (
    <div className={fullBleed ? 'relative' : 'mb-1'}>
      <div className={wrapperClass} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div ref={trackRef} className="flex" style={{ width: `${cloned.length * 100}%`, willChange: 'transform' }} onTransitionEnd={handleTransitionEnd}>
          {cloned.map((slide, i) => (
            <div
              key={i}
              className={slideClass}
              style={{ width: `${100 / cloned.length}%`, flexShrink: 0 }}
              onClick={() => { if (slide.cta) { const cat = categories.find(c => c.name.toLowerCase() === slide.cta.toLowerCase()); onNavigate('categorydetail', { category: cat || { name: slide.cta } }); } }}
            >
              {slide.image
                ? <img src={slide.image?.includes('res.cloudinary.com') ? slide.image.replace('/upload/', '/upload/w_1200,c_limit,f_auto,q_auto/') : slide.image} alt="slide" loading="eager" fetchpriority="high" className="w-full h-full object-cover" draggable={false} />
                : <div className="w-full h-full bg-gradient-to-br from-[#FEF0E6] to-[#F47B20]/20 flex items-center justify-center">
                    <span className="text-[#F47B20] text-sm font-medium">Hero Image</span>
                  </div>
              }
              {/* Subtle gradient overlay at bottom */}
              {fullBleed && <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />}
            </div>
          ))}
        </div>
      </div>
      {total > 1 && (
        <div className={`flex justify-center items-center gap-1.5 ${fullBleed ? 'absolute bottom-4 left-0 right-0' : 'mt-2'}`}>
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i + 1, true)}
              className={`rounded-full transition-all duration-300 ${activeDot === i
                ? 'w-6 h-2 bg-white shadow-md'
                : 'w-2 h-2 bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Promo Carousel (mobile only)
   ───────────────────────────────────────────────────────────── */
function PromoCarousel({ banners, categories, onNavigate }) {
  const total = banners.length;
  const cloned = total > 0 ? [banners[total - 1], ...banners, banners[0]] : [];
  const [promoIdx, setPromoIdx] = useState(1);
  const trackRef = useRef(null);
  const timerRef = useRef(null);

  const goTo = useCallback((idx, animate = true) => {
    const track = trackRef.current;
    if (!track) return;
    track.style.transition = animate ? 'transform 0.4s cubic-bezier(0.4,0,0.2,1)' : 'none';
    track.style.transform = `translateX(calc(-${idx * 82}vw + 9vw))`;
    setPromoIdx(idx);
  }, []);

  const handleTransitionEnd = () => {
    if (promoIdx >= cloned.length - 1) goTo(1, false);
    else if (promoIdx <= 0) goTo(total, false);
  };

  useEffect(() => {
    if (total <= 1) return;
    timerRef.current = setInterval(() => goTo(promoIdx + 1, true), 4000);
    return () => clearInterval(timerRef.current);
  }, [promoIdx, total, goTo]);

  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; clearInterval(timerRef.current); };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? goTo(promoIdx + 1, true) : goTo(promoIdx - 1, true);
    touchStartX.current = null;
  };

  if (total === 0) return null;

  return (
    <div className="mt-4 mb-2 overflow-hidden relative">
      <h3 className="px-4 text-[#F47B20] font-semibold text-xs mb-2 uppercase tracking-wide">Promo</h3>
      <div className="w-full" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div ref={trackRef} className="flex" style={{ willChange: 'transform', transform: `translateX(calc(-82vw + 9vw))` }} onTransitionEnd={handleTransitionEnd}>
          {cloned.map((b, i) => (
            <div key={i} onClick={() => { const target = b.cta || b.title; if (target) { const cat = categories.find(c => c.name.toLowerCase() === target.toLowerCase()); onNavigate('categorydetail', { category: cat || { name: target } }); } }} className="relative flex-shrink-0 rounded-2xl overflow-hidden shadow-sm cursor-pointer mx-[2vw] bg-gray-100" style={{ width: '78vw', aspectRatio: '16/5' }}>
              {b.image ? <img src={b.image?.includes('res.cloudinary.com') ? b.image.replace('/upload/', '/upload/w_800,c_limit,f_auto,q_auto/') : b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover" draggable={false} /> : <div className="w-full h-full bg-gradient-to-br from-[#F47B20] to-[#E05C00]" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Mobile Product Card
   ───────────────────────────────────────────────────────────── */
function ProductCard({ product, wishlist, onWishlist, onNavigate }) {
  const isWished = wishlist.includes(product.id);
  const coverImage = product.images?.find(img => !img.match(/\.(mp4|webm|ogg)$/i)) || product.image;
  // Resize to 600px wide for cards — Cloudinary serves much smaller files
  const thumbUrl = coverImage?.includes('res.cloudinary.com')
    ? coverImage.replace('/upload/', '/upload/w_600,c_limit/')
    : coverImage;

  return (
    <div onClick={() => onNavigate('productdetail', { product })} className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-0.5">
      <div className="relative aspect-[3/4] bg-gray-50 overflow-hidden">
        {coverImage ? (coverImage.match(/\.(mp4|webm|ogg)$/i) ? <video src={coverImage} autoPlay muted loop playsInline className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <img src={thumbUrl} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={e => { e.target.style.display = 'none'; }} />) : <div className="w-full h-full flex items-center justify-center"><GownIcon className="w-10 h-10 text-gray-200" /></div>}
        {product.stock === 0 && <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px] flex items-center justify-center z-10 pointer-events-none"><svg className="w-14 h-14 text-red-500/90 drop-shadow-md" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg></div>}
        {product.stock === 0 && <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded-full z-20">Sold Out</div>}
        {product.stock !== undefined && product.stock > 0 && <div className="absolute top-2 left-2 bg-amber-500/90 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded-full">{product.stock} left</div>}
        <button onClick={(e) => { e.stopPropagation(); onWishlist(product.id); }} className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-sm transition-all shadow-sm ${isWished ? 'bg-[#F47B20] text-white' : 'bg-white/80 text-gray-400 hover:text-[#F47B20] hover:bg-white'}`}>
          <HeartIcon className="w-3.5 h-3.5" filled={isWished} />
        </button>
      </div>
      <div className="px-3 pt-2.5 pb-3">
        <p className="text-[#1A1A2E] text-[11px] font-medium leading-snug line-clamp-2 mb-1 tracking-tight">{product.name}</p>
        {product.description ? <p className="text-gray-400 text-[10px] leading-snug line-clamp-1 mb-1.5">{product.description}</p> : null}
        <div className="flex items-baseline gap-1.5">
          <p className="font-bold text-[#1A1A2E] text-sm">{product.priceStr || `₦${Number(product.price || 0).toLocaleString()}`}</p>
          {product.original_price && product.original_price > product.price ? <p className="text-gray-400 text-[10px] line-through">{`₦${Number(product.original_price).toLocaleString()}`}</p> : null}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Desktop Product Card — premium, full-size
   ───────────────────────────────────────────────────────────── */
function DesktopProductCard({ product, wishlist, onWishlist, onNavigate }) {
  const isWished = wishlist.includes(product.id);
  const outOfStock = product.stock === 0;
  const coverImage = product.images?.find(img => !img.match(/\.(mp4|webm|ogg)$/i)) || product.image;
  const thumbUrl = coverImage?.includes('res.cloudinary.com')
    ? coverImage.replace('/upload/', '/upload/w_600,c_limit/')
    : coverImage;
  const discount = product.original_price && product.original_price > product.price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  return (
    <div
      onClick={() => onNavigate('productdetail', { product })}
      className={`group relative bg-white rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-[0_8px_30px_rgba(244,123,32,0.15)] transition-all duration-300 hover:-translate-y-1 border border-gray-100/80 hover:border-[#F47B20]/30 ${outOfStock ? 'opacity-75' : ''}`}
    >
      {/* Image */}
      <div className="relative aspect-[3/4] bg-gray-50 overflow-hidden">
        {coverImage
          ? (coverImage.match(/\.(mp4|webm|ogg)$/i)
              ? <video src={coverImage} autoPlay muted loop playsInline className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              : <img src={thumbUrl} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" onError={e => { e.target.style.display = 'none'; }} />
            )
          : <div className="w-full h-full flex items-center justify-center bg-gray-50"><GownIcon className="w-12 h-12 text-gray-200" /></div>
        }

        {/* Sold out overlay */}
        {outOfStock && (
          <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px] flex items-center justify-center z-10 pointer-events-none">
            <span className="bg-black/70 text-white text-xs font-bold px-4 py-1.5 rounded-full tracking-wide">SOLD OUT</span>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-20">
          {discount > 0 && !outOfStock && (
            <span className="bg-[#F47B20] text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-md">
              -{discount}%
            </span>
          )}
          {!outOfStock && product.stock > 0 && product.stock <= 5 && (
            <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
              {product.stock} left
            </span>
          )}
        </div>

        {/* Wishlist */}
        <button
          onClick={e => { e.stopPropagation(); onWishlist(product.id); }}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-sm transition-all duration-200 shadow-md z-20
            ${isWished
              ? 'bg-[#F47B20] text-white scale-110'
              : 'bg-white/90 text-gray-400 hover:text-[#F47B20] hover:bg-white opacity-0 group-hover:opacity-100 hover:scale-110'
            }`}
        >
          <HeartIcon className="w-4 h-4" filled={isWished} />
        </button>

        {/* Quick view overlay */}
        <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out z-20">
          <div className="bg-[#1A1A2E]/90 backdrop-blur-sm text-white text-xs font-bold text-center py-3 tracking-wider uppercase">
            View Details
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <p className="text-[#1A1A2E] text-sm font-semibold leading-snug line-clamp-2 mb-1.5 tracking-tight group-hover:text-[#F47B20] transition-colors duration-200">
          {product.name}
        </p>
        {product.description && (
          <p className="text-gray-400 text-xs leading-snug line-clamp-1 mb-2">{product.description}</p>
        )}
        <div className="flex items-baseline gap-2 mt-1">
          <p className="font-black text-[#F47B20] text-base">
            {product.priceStr || `₦${Number(product.price || 0).toLocaleString()}`}
          </p>
          {product.original_price && product.original_price > product.price && (
            <p className="text-gray-400 text-xs line-through">
              {`₦${Number(product.original_price).toLocaleString()}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Shop Page
   ───────────────────────────────────────────────────────────── */
export default function ShopPage({ onNavigate, products, banners = [], heroSlides = [], categories = [], isFetching = false, wishlistIds = [], onToggleWishlist }) {
  const [localSearch, setLocalSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [sortBy, setSortBy] = useState('Default');
  const [activeCategory, setActiveCategory] = useState(null);
  const filterRefMobile = useRef(null);
  const filterRefDesktop = useRef(null);
  const catScrollRef = useRef(null);
  const activeSlides = (heroSlides || []).length > 0 ? heroSlides : [];
  const activeBanners = (banners || []).filter(b => b.status === 'active');

  const featuredSections = (categories || []).map((cat, i) => ({
    id: String(cat.id),
    label: cat.name,
    image: cat.image_url || DEFAULT_CATEGORY_IMAGES[i % DEFAULT_CATEGORY_IMAGES.length],
    rawCat: cat,
  }));

  /* ── Category continuous infinite auto-scroll (mobile) ── */
  useEffect(() => {
    const el = catScrollRef.current;
    if (!el || featuredSections.length === 0) return;
    let animId;
    let isPaused = false;
    let currentScroll = el.scrollLeft;

    const step = () => {
      if (!isPaused) {
        currentScroll += 0.8;
        if (currentScroll >= el.scrollWidth / 4) currentScroll -= el.scrollWidth / 4;
        el.scrollLeft = currentScroll;
      } else {
        currentScroll = el.scrollLeft;
      }
      animId = requestAnimationFrame(step);
    };

    animId = requestAnimationFrame(step);
    let resumeTimer;
    const pause = () => { isPaused = true; };
    const resume = () => { clearTimeout(resumeTimer); resumeTimer = setTimeout(() => { isPaused = false; }, 1500); };

    el.addEventListener('touchstart', pause, { passive: true });
    el.addEventListener('touchend', resume, { passive: true });
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', () => { isPaused = false; });

    return () => {
      cancelAnimationFrame(animId);
      clearTimeout(resumeTimer);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchend', resume);
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', () => { isPaused = false; });
    };
  }, [featuredSections.length]);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        (filterRefMobile.current && filterRefMobile.current.contains(e.target)) ||
        (filterRefDesktop.current && filterRefDesktop.current.contains(e.target))
      ) {
        return;
      }
      setShowFilter(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    if (localSearch.trim()) onNavigate('search', { query: localSearch });
  };

  const suggestions = localSearch.trim()
    ? (products || []).filter(p => p.name.toLowerCase().includes(localSearch.toLowerCase())).slice(0, 5)
    : [];

  const displayedProducts = [...(products || []).filter(p => {
    if (p.status === 'draft') return false;
    if (activeCategory && p.category_id !== activeCategory) return false;
    return true;
  })].sort((a, b) => {
    if (sortBy === 'Price: Low to High') return (a.price || 0) - (b.price || 0);
    if (sortBy === 'Price: High to Low') return (b.price || 0) - (a.price || 0);
    if (sortBy === 'Name A-Z') return (a.name || '').localeCompare(b.name || '');
    return 0;
  });

  return (
    <div className="bg-[#FAF3F0] min-h-screen">
      {isFetching && (
        <div className="flex items-center justify-center gap-2 py-1.5 px-4 bg-[#FEF0E6] border-b border-[#F47B20]/20">
          <div className="w-3 h-3 border-2 border-[#F47B20] border-t-transparent rounded-full animate-spin" />
          <span className="text-xs font-medium text-[#F47B20]">Loading…</span>
        </div>
      )}

      {/* ════ MOBILE LAYOUT (hidden on lg+) ════ */}
      <div className="lg:hidden">
        <div className="px-4 pt-3 pb-2.5">
          <div className="flex gap-2 items-center">
            <form onSubmit={handleSearchSubmit} className="flex-1 relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                id="shop-search-mobile"
                className="w-full pl-8 pr-3 py-2 rounded-xl bg-white border border-gray-100 text-sm outline-none focus:border-[#F47B20] shadow-sm placeholder-gray-400"
                placeholder="Search bridal wear…"
                value={localSearch}
                onChange={e => { setLocalSearch(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              />
              {showSuggestions && localSearch.trim() && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                  {suggestions.length > 0 ? suggestions.map(s => (
                    <div key={s.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowSuggestions(false); onNavigate('productdetail', { product: s }); }} className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0 flex items-center gap-3">
                      <img src={s.image || (s.images && s.images[0])} alt={s.name} className="w-10 h-10 object-cover rounded-lg" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">{s.name}</p>
                        <p className="text-xs text-gray-500">{s.priceStr || `₦${Number(s.price).toLocaleString()}`}</p>
                      </div>
                    </div>
                  )) : (
                    <div className="p-4 text-center text-sm text-gray-500">No products found</div>
                  )}
                </div>
              )}
            </form>
            <div className="relative" ref={filterRefMobile}>
              <button id="filter-btn-mobile" onClick={() => setShowFilter(v => !v)} className={`flex items-center gap-1 px-2.5 py-2 rounded-xl border text-xs font-medium transition-colors shadow-sm ${showFilter ? 'bg-[#F47B20] text-white border-[#F47B20]' : 'bg-white border-gray-100 text-gray-600 hover:border-[#F47B20]'}`}>
                <FilterIcon className="w-3.5 h-3.5" /><span>Sort</span>
              </button>
              {showFilter && (
                <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                  <div className="p-2.5">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 px-1">Sort by</p>
                    {SORT_OPTIONS.map(opt => (
                      <button key={opt} onClick={() => { setSortBy(opt); setShowFilter(false); }} className={`w-full text-left text-xs px-3 py-2 rounded-xl transition-colors ${sortBy === opt ? 'bg-[#FEF0E6] text-[#F47B20] font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>{opt}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <HeroCarousel slides={activeSlides} categories={categories} onNavigate={onNavigate} />
        {activeBanners.length > 0 && <PromoCarousel banners={activeBanners} categories={categories} onNavigate={onNavigate} />}
        {featuredSections.length > 0 && (
          <div className="mt-5 mb-4">
            <div className="px-4 mb-3"><h3 className="text-xs font-bold text-[#1A1A2E] uppercase tracking-widest">Shop by Category</h3></div>
            <div ref={catScrollRef} className="flex gap-3 px-4 pb-3 pt-1" style={{ overflowX: 'scroll', WebkitOverflowScrolling: 'touch', scrollBehavior: 'auto', scrollbarWidth: 'none' }}>
              {[...featuredSections, ...featuredSections, ...featuredSections, ...featuredSections].map((section, idx) => (
                <button key={`${section.id}-${idx}`} aria-hidden={idx >= featuredSections.length} onClick={() => onNavigate('categorydetail', { category: section.rawCat })} className="group flex-shrink-0 flex flex-col items-center gap-1.5 focus:outline-none">
                  <div className="relative overflow-hidden rounded-2xl shadow-sm" style={{ width: '72px', height: '72px' }}>
                    <img src={section.image?.includes('res.cloudinary.com') ? section.image.replace('/upload/', '/upload/w_400,c_limit,f_auto,q_auto/') : section.image} alt={section.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" onError={e => { e.target.style.display = 'none'; }} draggable={false} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="px-4 pb-28">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[#1A1A2E]">All Products</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {displayedProducts.length > 0 ? displayedProducts.map(product => (
              <ProductCard key={product.id} product={product} wishlist={wishlistIds} onWishlist={onToggleWishlist} onNavigate={onNavigate} />
            )) : (
              <div className="col-span-full text-center py-16 bg-white rounded-3xl border border-gray-100 shadow-sm mt-4">
                <SearchIcon className="w-7 h-7 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-semibold text-sm">No products found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ════ DESKTOP LAYOUT ════ */}
      <div className="hidden lg:block">

        {/* ── Full-bleed hero ── */}
        <div className="w-full relative">
          <HeroCarousel slides={activeSlides} categories={categories} onNavigate={onNavigate} fullBleed={true} />
        </div>

        {/* ── Category strip ── */}
        {featuredSections.length > 0 && (
          <div className="bg-white border-b border-gray-100 shadow-sm">
            <div className="max-w-[1600px] mx-auto px-8 py-5">
              <div className="flex items-center gap-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                <button
                  onClick={() => setActiveCategory(null)}
                  className={`flex-shrink-0 flex flex-col items-center gap-2 group focus:outline-none`}
                >
                  <div className={`w-16 h-16 rounded-2xl overflow-hidden border-2 transition-all duration-200 shadow-sm flex items-center justify-center
                    ${activeCategory === null ? 'border-[#F47B20] shadow-[0_0_0_3px_rgba(244,123,32,0.15)]' : 'border-gray-100 group-hover:border-[#F47B20]/50'}`}>
                    <span className="text-2xl">🛍</span>
                  </div>
                  <span className={`text-[11px] font-semibold transition-colors leading-tight text-center whitespace-nowrap
                    ${activeCategory === null ? 'text-[#F47B20]' : 'text-gray-600 group-hover:text-[#F47B20]'}`}>
                    All
                  </span>
                </button>
                {featuredSections.map((section) => (
                  <button
                    key={`dt-cat-${section.id}`}
                    onClick={() => setActiveCategory(activeCategory === section.rawCat.id ? null : section.rawCat.id)}
                    className="flex-shrink-0 flex flex-col items-center gap-2 group focus:outline-none"
                  >
                    <div className={`w-16 h-16 rounded-2xl overflow-hidden border-2 transition-all duration-200 shadow-sm
                      ${activeCategory === section.rawCat.id
                        ? 'border-[#F47B20] shadow-[0_0_0_3px_rgba(244,123,32,0.15)] scale-105'
                        : 'border-gray-100 group-hover:border-[#F47B20]/50 group-hover:scale-105'}`}>
                      <img
                        src={section.image}
                        alt={section.label}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <span className={`text-[11px] font-semibold transition-colors leading-tight text-center whitespace-nowrap max-w-[72px] truncate
                      ${activeCategory === section.rawCat.id ? 'text-[#F47B20]' : 'text-gray-600 group-hover:text-[#F47B20]'}`}>
                      {section.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Promo banners row (desktop) ── */}
        {activeBanners.length > 0 && (
          <div className="max-w-[1600px] mx-auto px-8 py-4">
            <div className={`grid gap-4 ${activeBanners.length === 1 ? 'grid-cols-1' : activeBanners.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {activeBanners.slice(0, 3).map(b => (
                <div
                  key={b.id}
                  onClick={() => { const target = b.cta || b.title; if (target) { const cat = categories.find(c => c.name.toLowerCase() === target.toLowerCase()); onNavigate('categorydetail', { category: cat || { name: target } }); } }}
                  className="relative rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 cursor-pointer aspect-[16/5]"
                >
                  {b.image
                    ? <img src={b.image?.includes('res.cloudinary.com') ? b.image.replace('/upload/', '/upload/w_1000,c_limit,f_auto,q_auto/') : b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-gradient-to-br from-[#F47B20] to-[#E05C00]" />
                  }
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Filter toolbar ── */}
        <div className="sticky top-[88px] z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
          <div className="max-w-[1600px] mx-auto px-8 py-3 flex items-center justify-between gap-4">
            {/* Left: title + count */}
            <div className="flex items-center gap-3">
              <h2 className="text-base font-black text-[#1A1A2E] tracking-tight">
                {activeCategory
                  ? featuredSections.find(s => s.rawCat.id === activeCategory)?.label || 'Products'
                  : 'All Products'
                }
              </h2>
              <span className="bg-[#FEF0E6] text-[#F47B20] text-xs font-bold px-2.5 py-0.5 rounded-full">
                {displayedProducts.length} items
              </span>
            </div>

            {/* Right: search + sort */}
            <div className="flex items-center gap-3">
              <form onSubmit={handleSearchSubmit} className="relative flex items-center">
                <SearchIcon className="absolute left-3 w-4 h-4 text-gray-400" />
                <input
                  className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 outline-none focus:border-[#F47B20] focus:bg-white transition-all w-52 placeholder-gray-400"
                  placeholder="Search products…"
                  value={localSearch}
                  onChange={e => { setLocalSearch(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                />
                {showSuggestions && localSearch.trim() && (
                  <div className="absolute top-full right-0 w-72 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    {suggestions.length > 0 ? suggestions.map(s => (
                      <div key={s.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowSuggestions(false); onNavigate('productdetail', { product: s }); }} className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0 flex items-center gap-3">
                        <img src={s.image || (s.images && s.images[0])} alt={s.name} className="w-10 h-10 object-cover rounded-lg" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-800 truncate">{s.name}</p>
                          <p className="text-xs text-gray-500">{s.priceStr || `₦${Number(s.price).toLocaleString()}`}</p>
                        </div>
                      </div>
                    )) : (
                      <div className="p-4 text-center text-sm text-gray-500">No products found</div>
                    )}
                  </div>
                )}
              </form>

              {/* Sort dropdown */}
              <div className="relative" ref={filterRefDesktop}>
                <button
                  onClick={() => setShowFilter(v => !v)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all shadow-sm ${showFilter || sortBy !== 'Default' ? 'bg-[#F47B20] text-white border-[#F47B20]' : 'bg-white border-gray-200 text-gray-700 hover:border-[#F47B20] hover:text-[#F47B20]'}`}
                >
                  <FilterIcon className="w-4 h-4" />
                  <span>{sortBy === 'Default' ? 'Sort' : sortBy}</span>
                  <svg className={`w-3.5 h-3.5 transition-transform ${showFilter ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showFilter && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                    <div className="p-2">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-3 pt-2">Sort by</p>
                      {SORT_OPTIONS.map(opt => (
                        <button
                          key={opt}
                          onClick={() => { setSortBy(opt); setShowFilter(false); }}
                          className={`w-full text-left text-sm px-3 py-2.5 rounded-xl transition-all flex items-center justify-between
                            ${sortBy === opt ? 'bg-[#FEF0E6] text-[#F47B20] font-bold' : 'text-gray-700 hover:bg-gray-50'}`}
                        >
                          {opt}
                          {sortBy === opt && <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Product grid ── */}
        <div className="max-w-[1600px] mx-auto px-8 py-8 pb-16">
          {displayedProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
              {displayedProducts.map(product => (
                <DesktopProductCard
                  key={product.id}
                  product={product}
                  wishlist={wishlistIds}
                  onWishlist={onToggleWishlist}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-32 bg-white rounded-3xl border border-gray-100 shadow-sm">
              <GownIcon className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-500 font-bold text-lg mb-1">No products found</p>
              <p className="text-gray-400 text-sm">Try a different category or search term</p>
              {activeCategory && (
                <button onClick={() => setActiveCategory(null)} className="mt-4 px-6 py-2 bg-[#F47B20] text-white rounded-full text-sm font-bold hover:bg-[#D96A10] transition-colors">
                  View All Products
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

